import { useEffect, useRef, useCallback } from "react";
import useCurrencyStore from "../store/useCurrencyStore";
import useOrgStore from "../store/useOrgStore";
import { getCurrencySymbol } from "../services/vehicleApis";
import { findMyOrg, GetMyDetails, getTermsAndConditions } from "../services/MyProfileApi";
import { useAuth } from "../contexts/AuthContext";
import useAppConfigStore from "../store/useAppConfigStore";
import useUserStore from "../store/useUserStore";
import useTermsStore from "../store/useTermsStore";
import { getUserBootstrap, FeatureListApi, getLatestReleasePopup, getSubscriptionFeatures } from "../services/CommonApi";
import useFeatureStore from "../store/useFeatureStore";
import useReleasePopupStore from "../store/useReleasePopupStore";
import { baseURL } from "../services/api";
import { checkFeedbackVisibility } from "../services/FeedbackApi";
import useFeedbackPopupStore from "../store/useFeedbackPopupStore";
import useSubscriptionFeatureStore from "../store/useSubscriptionFeatureStore";
import useBootstrapStore from "../store/useBootstrapStore";

export function useInitializeAppData() {
  const { isAuthenticated } = useAuth();
  const setCurrencySymbol = useCurrencyStore((state) => state.setCurrencySymbol);
  const setOrganization = useOrgStore((state) => state.setOrganization);
  const setAppConfig = useAppConfigStore((state) => state.setAppConfig);
  const setTerms = useTermsStore((state) => state.setTerms);
  const setUser = useUserStore((state) => state.setUser);
  const setFeatures = useFeatureStore((state) => state.setFeatures);
  const setReleasePopup = useReleasePopupStore((state) => state.setReleasePopup); // ← added
  const hasLoadedRef = useRef(false);

  const setFeatureCodes = useSubscriptionFeatureStore((state) => state.setFeatureCodes);
  const clearFeatureCodes = useSubscriptionFeatureStore((state) => state.clearFeatureCodes);
  const setBootstrapData = useBootstrapStore((state) => state.setBootstrapData);
  const clearBootstrap = useBootstrapStore((state) => state.clearBootstrap);

  const loadData = useCallback(async () => {
    try {
      const [
        bootstrapResponse,
        termsResponse,
        featureResponse,
        subscriptionFeatureResponse,
      ] = await Promise.all([
        getUserBootstrap(),
        getTermsAndConditions(),
        FeatureListApi(),
        getSubscriptionFeatures(),
      ]);

      const bootstrapData = bootstrapResponse?.data?.data || bootstrapResponse?.data;
      if (bootstrapData) {
        setBootstrapData(bootstrapData);
        
        // Update existing stores for backward compatibility
        if (bootstrapData.currency) {
          setCurrencySymbol({
            ...bootstrapData.currency,
            currency_symbol: bootstrapData.currency.symbol || "$",
            country: bootstrapData.currency.country || bootstrapData.organization?.country,
            country_id: bootstrapData.currency.country_id || bootstrapData.organization?.country_id,
            state: bootstrapData.currency.state || bootstrapData.organization?.state,
            state_id: bootstrapData.currency.state_id || bootstrapData.organization?.state_id,
          });
        }
        
        if (bootstrapData.organization) {
          const org = {
            ...bootstrapData.organization,
            country: bootstrapData.organization.country_id
              ? { id: bootstrapData.organization.country_id, name: bootstrapData.organization.country }
              : bootstrapData.organization.country,
            state: bootstrapData.organization.state_id
              ? { id: bootstrapData.organization.state_id, name: bootstrapData.organization.state }
              : bootstrapData.organization.state,
          };
          setOrganization(org);
          setAppConfig(org);
        }

        const userData = bootstrapData.user || {};
        if (userData?.profile_pic && !userData.profile_pic.startsWith("http")) {
          userData.profile_pic = `${baseURL.slice(0, -1)}/${userData.profile_pic.replace(/^\//, "")}`;
        }
        setUser(userData);
      }
      setTerms(termsResponse?.data?.data);

      const featureArray = featureResponse?.data?.data || [];

      const featuresObject = {};
      featureArray.forEach((feature) => {
        featuresObject[feature.name] = feature.is_enabled;
      });
      setFeatures(featuresObject);

      const codes = subscriptionFeatureResponse?.data?.feature_codes ?? [];
      setFeatureCodes(codes);

      const isReleasePopupEnabled = !!featuresObject["release_popup"];

      if (isReleasePopupEnabled) {
        try {
          const releaseResponse = await getLatestReleasePopup();

          const rawData = releaseResponse?.data || releaseResponse;
          const releaseData = rawData?.data || rawData;

          if (releaseData?.show_popup && releaseData?.release) {
            setReleasePopup(true, releaseData.release);
          } else {
            setReleasePopup(false, null);
          }

        } catch (error) {
          setReleasePopup(false, null);
        }
      } else {
        // 🔥 IMPORTANT: force close if disabled
        setReleasePopup(false, null);
      }

      // 🔹 Feedback popup logic
      const setFeedbackPopup =
        useFeedbackPopupStore.getState().setFeedbackPopup;

      const isFeedbackEnabled = featuresObject["feedback_popup"];

      if (isFeedbackEnabled) {
        try {
          const feedbackCheckResponse = await checkFeedbackVisibility();
          setFeedbackPopup(!!feedbackCheckResponse?.show);
        } catch (error) {
          setFeedbackPopup(false);
        }
      } else {
        setFeedbackPopup(false);
      }

      hasLoadedRef.current = true;
    } catch (error) {
      console.error("Error loading initial data:", error);
      setCurrencySymbol("$");
    }
  }, [
    setCurrencySymbol,
    setOrganization,
    setAppConfig,
    setUser,
    setTerms,
    setFeatures,
    setReleasePopup,
    setFeatureCodes,
    setBootstrapData,
  ]);

  useEffect(() => {
    if (isAuthenticated && !hasLoadedRef.current) {
      loadData();
    }
  }, [isAuthenticated, loadData]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasLoadedRef.current = false;
      clearFeatureCodes(); 
      clearBootstrap();
    }
  }, [isAuthenticated, clearFeatureCodes, clearBootstrap]);

  return { reloadAppData: loadData };
}
